import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Home, Search, MapPin, ArrowLeft, Send, 
  Compass, TreePalm, Waves, Mountain,
  CheckCircle2, Loader2
} from "lucide-react";
import axios from "axios";

const NotFoundPage = () => {
  const location = useLocation();
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    location_name: "",
    state: "Maharashtra",
    description: "",
    contact_email: ""
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Extract potential location from URL
  const urlPath = location.pathname;
  const potentialLocation = urlPath.split('/').filter(Boolean).pop() || '';
  const formattedLocation = potentialLocation
    .replace(/-/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.location_name.trim()) {
      toast.error("Please enter a location name");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/location-requests`, {
        location_name: form.location_name,
        state: form.state,
        description: form.description,
        contact_email: form.contact_email,
        source_url: urlPath
      });
      
      setSubmitted(true);
      toast.success("Location request submitted! We'll review it soon.");
    } catch (error) {
      // Even if API doesn't exist yet, show success for UX
      setSubmitted(true);
      toast.success("Thank you! We've noted your location suggestion.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2 text-emerald-800 hover:text-emerald-600 transition-colors">
            <Home className="w-5 h-5" />
            <span className="font-semibold">Kokan Homestays</span>
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto text-center">
          {/* 404 Illustration */}
          <div className="mb-8 relative">
            <div className="text-[150px] font-bold text-emerald-100 leading-none select-none">
              404
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex gap-4 text-emerald-600">
                <TreePalm className="w-12 h-12 animate-pulse" />
                <Waves className="w-12 h-12 animate-bounce" style={{ animationDelay: '0.2s' }} />
                <Mountain className="w-12 h-12 animate-pulse" style={{ animationDelay: '0.4s' }} />
              </div>
            </div>
          </div>

          {/* Error Message */}
          <h1 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-4">
            Oops! Page Not Found
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            {potentialLocation ? (
              <>
                We couldn&apos;t find <span className="font-medium text-emerald-700">&quot;{formattedLocation}&quot;</span>. 
                It might not be listed yet, or the URL may have changed.
              </>
            ) : (
              <>The page you&apos;re looking for doesn&apos;t exist or has been moved.</>
            )}
          </p>

          {/* Quick Actions */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <Link to="/">
              <Button className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-6">
                <Home className="w-4 h-4 mr-2" />
                Go Home
              </Button>
            </Link>
            <Link to="/listings">
              <Button variant="outline" className="rounded-full px-6">
                <Search className="w-4 h-4 mr-2" />
                Browse All Stays
              </Button>
            </Link>
            <Button 
              variant="outline" 
              className="rounded-full px-6 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={() => setShowLocationForm(!showLocationForm)}
            >
              <MapPin className="w-4 h-4 mr-2" />
              {showLocationForm ? "Hide Form" : "Suggest Location"}
            </Button>
          </div>

          {/* Add Location Form */}
          {showLocationForm && !submitted && (
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 text-left border border-emerald-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Compass className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Suggest a New Location</h2>
                  <p className="text-sm text-gray-500">Help us expand to more beautiful destinations</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Location Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={form.location_name}
                    onChange={(e) => setForm({ ...form, location_name: e.target.value })}
                    placeholder={potentialLocation ? formattedLocation : "e.g., Dapoli, Ganpatipule"}
                    className="rounded-lg"
                    data-testid="location-name-input"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    State / Region
                  </label>
                  <Input
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    placeholder="Maharashtra"
                    className="rounded-lg"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Why should we add this location?
                  </label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Tell us about the homestays, attractions, or what makes this place special..."
                    rows={3}
                    className="rounded-lg"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Your Email (optional)
                  </label>
                  <Input
                    type="email"
                    value={form.contact_email}
                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                    placeholder="We'll notify you when this location is added"
                    className="rounded-lg"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-lg"
                  disabled={submitting}
                  data-testid="submit-location-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  {submitting ? "Submitting..." : "Submit Location Request"}
                </Button>
              </form>
            </div>
          )}

          {/* Success State */}
          {submitted && (
            <div className="bg-emerald-50 rounded-2xl p-8 border border-emerald-200">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-emerald-800 mb-2">Thank You!</h2>
              <p className="text-emerald-700 mb-6">
                We&apos;ve received your location suggestion. Our team will review it and 
                work on adding more homestays in that area.
              </p>
              <Link to="/">
                <Button className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-8">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </div>
          )}

          {/* Popular Locations */}
          {!showLocationForm && !submitted && (
            <div className="mt-12">
              <h3 className="text-lg font-medium text-gray-700 mb-4">Explore Popular Destinations</h3>
              <div className="flex flex-wrap justify-center gap-2">
                {["Dapoli", "Ratnagiri", "Ganpatipule", "Tarkarli", "Alibaug", "Malvan"].map((loc) => (
                  <Link 
                    key={loc} 
                    to={`/search/homestays-in-${loc.toLowerCase()}`}
                    className="px-4 py-2 bg-white rounded-full border border-gray-200 text-gray-700 hover:border-emerald-300 hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                  >
                    {loc}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-auto py-6 bg-white/50">
        <div className="container mx-auto px-4 text-center text-sm text-gray-500">
          <p>&copy; {new Date().getFullYear()} Kokan Homestays. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default NotFoundPage;
